import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import api from '../../services/api';
import { SELLER_CONVERSATION_PARAMS } from '../../utils/sellerScope';
import { normalizeMessages, normalizeOffer, NormalizedOffer } from '../../utils/offerNormalize';
import { LIVE_POLL, livePollInterval, LIVE_STALE_MS } from '../../config/liveSync';
import {
  sellerKeys,
  SellerOffersParams,
  SellerOrdersParams,
  SellerPaymentsParams,
  SellerProductsParams,
} from '../../lib/queryKeys';
import { invalidateSellerLiveData } from '../../lib/invalidateSellerLive';

const defaultQueryOptions = {
  staleTime: LIVE_STALE_MS,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  placeholderData: keepPreviousData,
};

export async function fetchSellerConversations() {
  const response = await api.get('/conversations', { params: SELLER_CONVERSATION_PARAMS });
  const list = response.data.data || [];
  return Array.isArray(list) ? list : [];
}

export function useSellerStats(enabled = true) {
  return useQuery({
    queryKey: sellerKeys.stats(),
    queryFn: async () => {
      const response = await api.get('/seller/stats');
      return response.data.data;
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.STATS),
  });
}

export function useSellerOrderApprovalSettings(enabled = true) {
  return useQuery({
    queryKey: sellerKeys.orderApproval(),
    queryFn: async () => {
      const response = await api.get('/seller/settings/order-approval');
      return Boolean(response.data?.data?.sellerCanApproveOrders);
    },
    enabled,
    staleTime: LIVE_POLL.SETTINGS,
    refetchInterval: () => livePollInterval(LIVE_POLL.SETTINGS),
  });
}

export function useSellerOrders(params: SellerOrdersParams, enabled = true) {
  return useQuery({
    queryKey: sellerKeys.orders(params),
    queryFn: async () => {
      const apiParams: Record<string, string | number> = {
        page: params.page,
        limit: params.limit,
      };
      if (params.status) apiParams.status = params.status;
      if (params.search) apiParams.search = params.search;
      const response = await api.get('/seller/orders', { params: apiParams });
      return response.data.data;
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.LIST),
  });
}

export function useSellerPayments(params: SellerPaymentsParams, enabled = true) {
  return useQuery({
    queryKey: sellerKeys.payments(params),
    queryFn: async () => {
      const apiParams: Record<string, string | number> = {
        page: params.page,
        limit: params.limit,
      };
      if (params.search) apiParams.search = params.search;
      if (params.dateFrom) apiParams.dateFrom = params.dateFrom;
      if (params.dateTo) apiParams.dateTo = params.dateTo;
      const response = await api.get('/seller/payments', { params: apiParams });
      return response.data.data;
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.LIST),
  });
}

export function useSellerProducts(params: SellerProductsParams, enabled = true) {
  return useQuery({
    queryKey: sellerKeys.products(params),
    queryFn: async () => {
      const apiParams: Record<string, string> = {
        page: String(params.page),
        limit: String(params.limit),
      };
      if (params.search) apiParams.search = params.search;
      if (params.isActive) apiParams.isActive = params.isActive;
      if (params.category) apiParams.category = params.category;
      const response = await api.get('/seller/products', { params: apiParams });
      return response.data.data;
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.LIST),
  });
}

export function useSellerOffers(params: SellerOffersParams, enabled = true) {
  return useQuery({
    queryKey: sellerKeys.offers(params),
    queryFn: async () => {
      const apiParams: Record<string, string | number> = {
        page: params.page,
        limit: params.limit,
      };
      if (params.status) apiParams.status = params.status;
      const response = await api.get('/offers/seller', { params: apiParams });
      const data = response.data.data;
      const raw = data.offers || data.bids || [];
      const offers = Array.isArray(raw)
        ? (raw
            .map((o: Record<string, unknown>) => normalizeOffer(o))
            .filter(Boolean) as NormalizedOffer[])
        : [];
      return { offers, totalPages: data.totalPages || 1 };
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.LIST),
  });
}

export function useSellerConversations(enabled = true) {
  return useQuery({
    queryKey: sellerKeys.conversations(),
    queryFn: fetchSellerConversations,
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.BADGE),
  });
}

export function useSellerConversationMessages(conversationId: string | null, enabled = true) {
  return useQuery({
    queryKey: sellerKeys.conversationMessages(conversationId || ''),
    queryFn: async () => {
      if (!conversationId) return [];
      const response = await api.get(`/conversations/${conversationId}/messages`, {
        params: SELLER_CONVERSATION_PARAMS,
      });
      const list = response.data.data || [];
      return normalizeMessages(Array.isArray(list) ? list : []) as unknown[];
    },
    enabled: enabled && !!conversationId,
    staleTime: LIVE_STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: () =>
      conversationId ? livePollInterval(LIVE_POLL.CHAT) : false,
    placeholderData: keepPreviousData,
  });
}

export function useSellerPendingOfferCount(enabled = true) {
  return useQuery({
    queryKey: sellerKeys.pendingOfferCount(),
    queryFn: async () => Number((await api.get('/offers/seller/pending-count')).data?.data?.count ?? 0),
    enabled,
    staleTime: LIVE_STALE_MS,
    refetchOnWindowFocus: false,
    refetchInterval: () => livePollInterval(LIVE_POLL.BADGE),
    placeholderData: keepPreviousData,
  });
}

export function useSellerLiveInvalidation() {
  const queryClient = useQueryClient();
  return {
    invalidateAll: () => invalidateSellerLiveData(queryClient, 'all'),
    invalidateMessages: () => invalidateSellerLiveData(queryClient, 'message'),
    invalidateOffers: () => invalidateSellerLiveData(queryClient, 'offer'),
    invalidateOrders: () => invalidateSellerLiveData(queryClient, 'order'),
    invalidatePayments: () => invalidateSellerLiveData(queryClient, 'payment'),
    queryClient,
  };
}
